import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Search,
  Upload,
  Edit2,
  Trash2,
  Building2,
  User,
  MapPin,
  Zap,
  Link2,
  FileText,
  Eye,
  Download,
  Network,
  MoreHorizontal,
  ChevronRight,
  AlertTriangle,
  Check,
  X,
  ExternalLink,
  Calendar,
  Hash,
  Briefcase,
  Scale,
  FileWarning,
  Globe,
  Landmark,
  Home,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { ScrollArea } from '../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { intelligenceAPI, geocodeAPI, fieldOptionsAPI } from '../lib/api';
import { formatDate, formatNumber, formatDateTime, getRecordTypeLabel, cn } from '../lib/utils';
import { toast } from 'sonner';
import { EventModal, DocumentModal, LocationModal, PropertyModal } from '../components/modals/DIModals';
import { RecordPreviewSheet } from '../components/RecordPreviewSheet';
import { OrganisationAutocomplete } from '../components/OrganisationAutocomplete';
import { DynamicSelect } from '../components/DynamicSelect';
import { RelationshipGraph } from '../components/RelationshipGraph';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
// Record type definitions with icons and colors
const RECORD_TYPES = [
  { value: 'organisation', label: 'Organisations', icon: Building2, color: 'bg-blue-500', buttonText: '+ Record Organisation' },
  { value: 'person', label: 'People', icon: User, color: 'bg-green-500', buttonText: '+ Record Person' },
  { value: 'location', label: 'Locations', icon: MapPin, color: 'bg-amber-500', buttonText: '+ Pin Location' },
  { value: 'property', label: 'Properties', icon: Home, color: 'bg-orange-500', buttonText: '+ Record Property' },
  { value: 'event', label: 'Events', icon: Zap, color: 'bg-purple-500', buttonText: '+ Log Event' },
  { value: 'relationship', label: 'Relationships', icon: Link2, color: 'bg-pink-500', buttonText: '+ Map Relationship' },
  { value: 'document', label: 'Documents', icon: FileText, color: 'bg-teal-500', buttonText: '+ File Document' },
  { value: 'graph', label: 'Graph View', icon: Network, color: 'bg-indigo-500', buttonText: null, isSpecial: true },
];

const COMPANY_TYPES = ['LTD', 'PLC', 'LLP', 'CIC', 'LBG', 'Other'];
const COMPANY_STATUSES = ['Active', 'Dissolved', 'Liquidation', 'Administration', 'Other'];
const SECTORS = ['Finance', 'Healthcare', 'Energy', 'Transport', 'Tech', 'Real Estate', 'Public Sector', 'Legal', 'Construction', 'Retail', 'Other'];
const SENIORITY_LEVELS = ['C-Suite', 'Director', 'Manager', 'Official', 'Other'];
const PERSON_TYPES = ['Executive', 'Director', 'Politician', 'Regulator', 'Journalist', 'Investor', 'Advisor', 'Other'];
const EVENT_TYPES = ['Funding', 'Acquisition', 'Merger', 'Lawsuit', 'Regulatory Action', 'Planning Permission', 'Insolvency', 'Board Change', 'Procurement Award', 'Contract Award', 'Partnership', 'Product Launch', 'Closure', 'IPO', 'Enforcement Notice', 'Policy Change', 'Other'];
const RELATIONSHIP_TYPES = ['Director Of', 'Shareholder In', 'Investor In', 'Owns Property', 'Regulates', 'Litigating Against', 'Contracting With', 'Partnered With', 'Parent Of', 'Subsidiary Of', 'Supplier To', 'Employed By', 'Advisor To', 'Other'];
const DOCUMENT_TYPES = ['Annual Report', 'Court Filing', 'Gazette Notice', 'Planning Doc', 'FOI Response', 'Press Release', 'Regulatory Notice', 'Land Registry Filing', 'Companies House Filing', 'Contract', 'Procurement Notice', 'Insolvency Notice', 'Other'];
const SOURCE_TYPES = ['Manual', 'Companies House', 'Land Registry', 'Gazette', 'CSV Import', 'Automated Story', 'Web', 'Other'];

// Entity Type Badge Component
function EntityTypeBadge({ type, size = 'sm' }) {
  const config = {
    organisation: { icon: Building2, label: 'Org', color: 'bg-blue-900/30 text-blue-400 border-blue-800' },
    person: { icon: User, label: 'Person', color: 'bg-green-900/30 text-green-400 border-green-800' },
    property: { icon: Home, label: 'Property', color: 'bg-orange-900/30 text-orange-400 border-orange-800' },
    location: { icon: MapPin, label: 'Location', color: 'bg-amber-900/30 text-amber-400 border-amber-800' },
    event: { icon: Zap, label: 'Event', color: 'bg-purple-900/30 text-purple-400 border-purple-800' },
    relationship: { icon: Link2, label: 'Relationship', color: 'bg-pink-900/30 text-pink-400 border-pink-800' },
    document: { icon: FileText, label: 'Document', color: 'bg-teal-900/30 text-teal-400 border-teal-800' },
  };
  
  const { icon: Icon, label, color } = config[type] || config.organisation;
  const sizeClasses = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-1';
  
  return (
    <Badge className={cn('border font-normal', color, sizeClasses)}>
      <Icon className={cn('mr-1', size === 'sm' ? 'w-3 h-3' : 'w-4 h-4')} />
      {label}
    </Badge>
  );
}

// Confidence Score Component
function ConfidenceScore({ score }) {
  if (score === null || score === undefined) return <span className="text-text-tertiary">-</span>;
  
  const percentage = Math.round(score * 100);
  const bars = Math.round(score * 5);
  const color = percentage >= 75 ? 'text-green-400' : percentage >= 40 ? 'text-amber-400' : 'text-red-400';
  const bgColor = percentage >= 75 ? 'bg-green-400' : percentage >= 40 ? 'bg-amber-400' : 'bg-red-400';
  
  return (
    <div className={cn('flex items-center gap-1.5 font-mono text-xs', color)}>
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <div key={i} className={cn('w-1.5 h-3 rounded-sm', i < bars ? bgColor : 'bg-bg-tertiary')} />
        ))}
      </div>
      <span>{percentage}%</span>
    </div>
  );
}

// Risk Signal Badges Component
function RiskSignals({ content }) {
  const signals = [];
  
  if (content?.risk?.insolvency_flag) {
    signals.push({ label: 'Insolvent', color: 'bg-red-900/50 text-red-300 border-red-800' });
  }
  if (content?.risk?.charges_count > 0) {
    signals.push({ label: `Charges (${content.risk.charges_count})`, color: 'bg-orange-900/50 text-orange-300 border-orange-800' });
  }
  if (content?.risk?.accounts_overdue) {
    signals.push({ label: 'Accts OD', color: 'bg-amber-900/50 text-amber-300 border-amber-800' });
  }
  if (content?.risk?.cs_overdue) {
    signals.push({ label: 'CS OD', color: 'bg-amber-900/50 text-amber-300 border-amber-800' });
  }
  if (content?.risk?.regulatory_action_flag) {
    signals.push({ label: 'Regulatory', color: 'bg-blue-900/50 text-blue-300 border-blue-800' });
  }
  if (content?.risk?.litigation_flag) {
    signals.push({ label: 'Litigation', color: 'bg-rose-900/50 text-rose-300 border-rose-800' });
  }
  
  if (signals.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1">
      {signals.map((signal, i) => (
        <Badge key={i} className={cn('text-xs border', signal.color)}>{signal.label}</Badge>
      ))}
    </div>
  );
}

// Multi-value Tag Input Component
function TagInput({ value = [], onChange, placeholder = 'Add tag...' }) {
  const [inputValue, setInputValue] = useState('');
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (!value.includes(inputValue.trim())) {
        onChange([...value, inputValue.trim()]);
      }
      setInputValue('');
    }
  };
  
  const removeTag = (tagToRemove) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };
  
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[32px] p-2 bg-bg-tertiary border border-border-default rounded-lg">
        {value.map((tag, i) => (
          <Badge key={i} variant="secondary" className="bg-accent-primary/20 text-accent-primary gap-1">
            {tag}
            <X className="w-3 h-3 cursor-pointer hover:text-white" onClick={() => removeTag(tag)} />
          </Badge>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[100px] bg-transparent outline-none text-sm text-text-primary placeholder:text-text-tertiary"
        />
      </div>
      <p className="text-xs text-text-tertiary">Press Enter to add</p>
    </div>
  );
}

// Modal Tab Navigation Component
function ModalTabs({ tabs, activeTab, onTabChange }) {
  return (
    <div className="flex border-b border-border-default mb-4 -mx-6 px-6">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-[1px] transition-colors',
            activeTab === tab.id
              ? 'border-accent-primary text-accent-primary'
              : 'border-transparent text-text-tertiary hover:text-text-secondary'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// Organisation Modal Component
function OrganisationModal({ open, onOpenChange, record, onSave }) {
  const [activeTab, setActiveTab] = useState('identity');
  const [form, setForm] = useState({
    title: '',
    companies_house_number: '',
    content: {
      identity: {
        legal_name: '',
        trading_names: [],
        company_type: '',
        company_status: '',
        incorporation_date: '',
        sic_codes: [],
        sector: '',
        subsector: '',
      },
      address: {
        address_line1: '',
        address_line2: '',
        town_city: '',
        postcode: '',
        postcode_area: '',
        local_authority: '',
        region: '',
        country: 'England',
        parliamentary_constituency: '',
        lsoa_code: '',
        msoa_code: '',
        lat: null,
        lng: null,
      },
      risk: {
        insolvency_flag: false,
        insolvency_type: '',
        charges_count: 0,
        accounts_last_made_up_to: '',
        accounts_next_due: '',
        accounts_overdue: false,
        cs_last_made_up_to: '',
        cs_next_due: '',
        cs_overdue: false,
        regulatory_action_flag: false,
        litigation_flag: false,
      },
      pscs: [],
      links: {
        companies_house_url: '',
        website: '',
        linked_organisation_id: '',
        vat_number: '',
        charity_number: '',
      },
    },
    tags: [],
    source: 'Manual',
    source_url: '',
    confidence_score: 0.8,
  });
  
  const [geocoding, setGeocoding] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  useEffect(() => {
    if (record) {
      setForm({
        title: record.title || '',
        companies_house_number: record.companies_house_number || '',
        content: {
          identity: { ...form.content.identity, ...record.content?.identity },
          address: { ...form.content.address, ...record.content?.address },
          risk: { ...form.content.risk, ...record.content?.risk },
          pscs: record.content?.pscs || [],
          links: { ...form.content.links, ...record.content?.links },
        },
        tags: record.tags || [],
        source: record.source || 'Manual',
        source_url: record.content?.source_url || '',
        confidence_score: record.content?.confidence_score || 0.8,
      });
    } else {
      // Reset form
      setForm({
        title: '',
        companies_house_number: '',
        content: {
          identity: { legal_name: '', trading_names: [], company_type: '', company_status: '', incorporation_date: '', sic_codes: [], sector: '', subsector: '' },
          address: { address_line1: '', address_line2: '', town_city: '', postcode: '', postcode_area: '', local_authority: '', region: '', country: 'England', parliamentary_constituency: '', lsoa_code: '', msoa_code: '', lat: null, lng: null },
          risk: { insolvency_flag: false, insolvency_type: '', charges_count: 0, accounts_last_made_up_to: '', accounts_next_due: '', accounts_overdue: false, cs_last_made_up_to: '', cs_next_due: '', cs_overdue: false, regulatory_action_flag: false, litigation_flag: false },
          pscs: [],
          links: { companies_house_url: '', website: '', linked_organisation_id: '', vat_number: '', charity_number: '' },
        },
        tags: [],
        source: 'Manual',
        source_url: '',
        confidence_score: 0.8,
      });
    }
    setActiveTab('identity');
    setDuplicateWarning(null);
  }, [record, open]);

  const handleGeocode = async () => {
    if (!form.content.address.postcode) return;
    
    setGeocoding(true);
    try {
      const response = await geocodeAPI.geocode(form.content.address.postcode);
      const data = response.data;
      
      setForm(prev => ({
        ...prev,
        content: {
          ...prev.content,
          address: {
            ...prev.content.address,
            postcode_area: data.postcode_area || '',
            local_authority: data.local_authority_name || '',
            region: data.region || '',
            country: data.country || 'England',
            parliamentary_constituency: data.parliamentary_constituency || '',
            lsoa_code: data.lsoa_code || '',
            msoa_code: data.msoa_code || '',
            lat: data.lat,
            lng: data.lng,
          }
        }
      }));
      toast.success('Postcode geocoded successfully');
    } catch (error) {
      toast.error('Failed to geocode postcode');
    } finally {
      setGeocoding(false);
    }
  };

  const handleCheckDuplicate = async () => {
    if (!form.companies_house_number) return;
    
    try {
      const response = await intelligenceAPI.getAll({
        record_type: 'organisation',
        search: form.companies_house_number,
        page_size: 1,
      });
      
      if (response.data.total > 0 && response.data.records[0].id !== record?.id) {
        setDuplicateWarning(response.data.records[0]);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error('Failed to check duplicate:', error);
    }
  };

  const addPSC = () => {
    setForm(prev => ({
      ...prev,
      content: {
        ...prev.content,
        pscs: [...prev.content.pscs, { name: '', nature_of_control: [], notified_on: '', nationality: '', country_of_residence: '' }]
      }
    }));
  };

  const updatePSC = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      content: {
        ...prev.content,
        pscs: prev.content.pscs.map((psc, i) => i === index ? { ...psc, [field]: value } : psc)
      }
    }));
  };

  const removePSC = (index) => {
    setForm(prev => ({
      ...prev,
      content: {
        ...prev.content,
        pscs: prev.content.pscs.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = () => {
    const data = {
      title: form.content.identity.legal_name || form.title,
      record_type: 'organisation',
      companies_house_number: form.companies_house_number,
      content: {
        schema_version: 'uk-intel-v1',
        ...form.content,
        confidence_score: form.confidence_score,
        source_url: form.source_url,
      },
      tags: form.tags,
      source: form.source,
    };
    onSave(data);
  };

  const tabs = [
    { id: 'identity', label: 'Core Identity' },
    { id: 'address', label: 'Registered Address' },
    { id: 'risk', label: 'Risk & Filing' },
    { id: 'pscs', label: 'PSCs' },
    { id: 'links', label: 'Links & Source' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-surface-card border-border-default max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-text-primary flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-400" />
            {record ? 'Edit Organisation' : 'Record Organisation'}
          </DialogTitle>
        </DialogHeader>
        
        <ModalTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        
        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            {/* Tab 1: Core Identity */}
            {activeTab === 'identity' && (
              <>
                <div>
                  <Label>Legal Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={form.content.identity.legal_name}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, legal_name: e.target.value } }
                    }))}
                    placeholder="Registered company name"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Trading Names</Label>
                  <TagInput
                    value={form.content.identity.trading_names}
                    onChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, trading_names: v } }
                    }))}
                    placeholder="Add trading name..."
                  />
                </div>
                
                <div>
                  <Label>Companies House Number</Label>
                  <Input
                    value={form.companies_house_number}
                    onChange={(e) => setForm(prev => ({ ...prev, companies_house_number: e.target.value }))}
                    onBlur={handleCheckDuplicate}
                    placeholder="e.g., 12345678"
                    className="bg-bg-tertiary border-border-default font-mono"
                  />
                  {duplicateWarning && (
                    <div className="mt-2 p-3 bg-amber-900/20 border border-amber-800 rounded-lg text-sm">
                      <div className="flex items-center gap-2 text-amber-400">
                        <AlertTriangle className="w-4 h-4" />
                        <span>A record with this CH number already exists:</span>
                      </div>
                      <p className="text-text-primary mt-1">{duplicateWarning.title}</p>
                      <div className="flex gap-2 mt-2">
                        <Button variant="outline" size="sm">View existing</Button>
                        <Button variant="ghost" size="sm" onClick={() => setDuplicateWarning(null)}>Continue anyway</Button>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Type</Label>
                    <Select
                      value={form.content.identity.company_type}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, company_type: v } }
                      }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Company Status</Label>
                    <Select
                      value={form.content.identity.company_status}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, company_status: v } }
                      }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Incorporation Date</Label>
                  <Input
                    type="date"
                    value={form.content.identity.incorporation_date}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, incorporation_date: e.target.value } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>SIC Codes</Label>
                  <TagInput
                    value={form.content.identity.sic_codes}
                    onChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, sic_codes: v } }
                    }))}
                    placeholder="Add SIC code..."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Sector</Label>
                    <DynamicSelect
                      fieldType="sector"
                      value={form.content.identity.sector}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, sector: v } }
                      }))}
                      placeholder="Select sector"
                      triggerClassName="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Subsector</Label>
                    <Input
                      value={form.content.identity.subsector}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, subsector: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Tags</Label>
                  <TagInput
                    value={form.tags}
                    onChange={(v) => setForm(prev => ({ ...prev, tags: v }))}
                    placeholder="Add tag..."
                  />
                </div>
              </>
            )}
            
            {/* Tab 2: Registered Address */}
            {activeTab === 'address' && (
              <>
                <div>
                  <Label>Address Line 1</Label>
                  <Input
                    value={form.content.address.address_line1}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, address: { ...prev.content.address, address_line1: e.target.value } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Address Line 2</Label>
                  <Input
                    value={form.content.address.address_line2}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, address: { ...prev.content.address, address_line2: e.target.value } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Town / City</Label>
                    <Input
                      value={form.content.address.town_city}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, address: { ...prev.content.address, town_city: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Postcode</Label>
                    <div className="flex gap-2">
                      <Input
                        value={form.content.address.postcode}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          content: { ...prev.content, address: { ...prev.content.address, postcode: e.target.value.toUpperCase() } }
                        }))}
                        placeholder="e.g., SW1A 2AA"
                        className="bg-bg-tertiary border-border-default font-mono"
                      />
                      <Button variant="outline" onClick={handleGeocode} disabled={geocoding}>
                        <MapPin className="w-4 h-4 mr-1" />
                        {geocoding ? 'Geocoding...' : 'Geocode'}
                      </Button>
                    </div>
                  </div>
                </div>
                
                {form.content.address.lat && (
                  <div className="p-4 bg-bg-tertiary rounded-lg space-y-2">
                    <p className="text-sm font-medium text-text-primary">Geocoded Data</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-text-tertiary">Postcode Area:</span> <span className="font-mono">{form.content.address.postcode_area}</span></div>
                      <div><span className="text-text-tertiary">Local Authority:</span> {form.content.address.local_authority}</div>
                      <div><span className="text-text-tertiary">Region:</span> {form.content.address.region}</div>
                      <div><span className="text-text-tertiary">Country:</span> {form.content.address.country}</div>
                      <div><span className="text-text-tertiary">Constituency:</span> {form.content.address.parliamentary_constituency}</div>
                      <div><span className="text-text-tertiary">LSOA:</span> <span className="font-mono text-xs">{form.content.address.lsoa_code}</span></div>
                      <div><span className="text-text-tertiary">MSOA:</span> <span className="font-mono text-xs">{form.content.address.msoa_code}</span></div>
                      <div><span className="text-text-tertiary">Lat/Lng:</span> <span className="font-mono text-xs">{form.content.address.lat}, {form.content.address.lng}</span></div>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label>Country</Label>
                  <Select
                    value={form.content.address.country}
                    onValueChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, address: { ...prev.content.address, country: v } }
                    }))}
                  >
                    <SelectTrigger className="bg-bg-tertiary border-border-default">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="England">England</SelectItem>
                      <SelectItem value="Scotland">Scotland</SelectItem>
                      <SelectItem value="Wales">Wales</SelectItem>
                      <SelectItem value="Northern Ireland">Northern Ireland</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {/* Tab 3: Risk & Filing */}
            {activeTab === 'risk' && (
              <>
                <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                  <div>
                    <Label className="text-base">Insolvency Flag</Label>
                    <p className="text-sm text-text-tertiary">Mark if company is in insolvency proceedings</p>
                  </div>
                  <Switch
                    checked={form.content.risk.insolvency_flag}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, risk: { ...prev.content.risk, insolvency_flag: v } }
                    }))}
                  />
                </div>
                
                {form.content.risk.insolvency_flag && (
                  <div>
                    <Label>Insolvency Type</Label>
                    <Select
                      value={form.content.risk.insolvency_type}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, risk: { ...prev.content.risk, insolvency_type: v } }
                      }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Administration">Administration</SelectItem>
                        <SelectItem value="Liquidation">Liquidation</SelectItem>
                        <SelectItem value="CVA">CVA</SelectItem>
                        <SelectItem value="Receivership">Receivership</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label>Charges Count</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.content.risk.charges_count}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, risk: { ...prev.content.risk, charges_count: parseInt(e.target.value) || 0 } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Accounts Last Made Up To</Label>
                    <Input
                      type="date"
                      value={form.content.risk.accounts_last_made_up_to}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, risk: { ...prev.content.risk, accounts_last_made_up_to: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Accounts Next Due</Label>
                    <Input
                      type="date"
                      value={form.content.risk.accounts_next_due}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, risk: { ...prev.content.risk, accounts_next_due: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                  <Label>Accounts Overdue</Label>
                  <Switch
                    checked={form.content.risk.accounts_overdue}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, risk: { ...prev.content.risk, accounts_overdue: v } }
                    }))}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Confirmation Statement Last Made Up To</Label>
                    <Input
                      type="date"
                      value={form.content.risk.cs_last_made_up_to}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, risk: { ...prev.content.risk, cs_last_made_up_to: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Confirmation Statement Next Due</Label>
                    <Input
                      type="date"
                      value={form.content.risk.cs_next_due}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, risk: { ...prev.content.risk, cs_next_due: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                  <Label>Confirmation Statement Overdue</Label>
                  <Switch
                    checked={form.content.risk.cs_overdue}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, risk: { ...prev.content.risk, cs_overdue: v } }
                    }))}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                  <div>
                    <Label className="text-base">Regulatory Action Flag</Label>
                    <p className="text-sm text-text-tertiary">Company has been subject to regulatory action</p>
                  </div>
                  <Switch
                    checked={form.content.risk.regulatory_action_flag}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, risk: { ...prev.content.risk, regulatory_action_flag: v } }
                    }))}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                  <div>
                    <Label className="text-base">Litigation Flag</Label>
                    <p className="text-sm text-text-tertiary">Company involved in legal proceedings</p>
                  </div>
                  <Switch
                    checked={form.content.risk.litigation_flag}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, risk: { ...prev.content.risk, litigation_flag: v } }
                    }))}
                  />
                </div>
              </>
            )}
            
            {/* Tab 4: PSCs */}
            {activeTab === 'pscs' && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Persons with Significant Control</Label>
                    <p className="text-sm text-text-tertiary">Add PSCs as registered at Companies House</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={addPSC}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add PSC
                  </Button>
                </div>
                
                {form.content.pscs.length === 0 ? (
                  <div className="text-center py-8 text-text-tertiary">
                    <User className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No PSCs added yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.content.pscs.map((psc, index) => (
                      <div key={index} className="p-4 bg-bg-tertiary rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">PSC {index + 1}</Label>
                          <Button variant="ghost" size="sm" onClick={() => removePSC(index)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                        <Input
                          value={psc.name}
                          onChange={(e) => updatePSC(index, 'name', e.target.value)}
                          placeholder="Full name"
                          className="bg-bg-default border-border-default"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={psc.nationality}
                            onChange={(e) => updatePSC(index, 'nationality', e.target.value)}
                            placeholder="Nationality"
                            className="bg-bg-default border-border-default"
                          />
                          <Input
                            value={psc.country_of_residence}
                            onChange={(e) => updatePSC(index, 'country_of_residence', e.target.value)}
                            placeholder="Country of Residence"
                            className="bg-bg-default border-border-default"
                          />
                        </div>
                        <Input
                          type="date"
                          value={psc.notified_on}
                          onChange={(e) => updatePSC(index, 'notified_on', e.target.value)}
                          className="bg-bg-default border-border-default"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {/* Tab 5: Links & Source */}
            {activeTab === 'links' && (
              <>
                <div>
                  <Label>Companies House URL</Label>
                  <Input
                    value={form.content.links.companies_house_url}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, links: { ...prev.content.links, companies_house_url: e.target.value } }
                    }))}
                    placeholder="https://find-and-update.company-information.service.gov.uk/company/..."
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Website</Label>
                  <Input
                    value={form.content.links.website}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, links: { ...prev.content.links, website: e.target.value } }
                    }))}
                    placeholder="https://..."
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>VAT Number</Label>
                    <Input
                      value={form.content.links.vat_number}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, links: { ...prev.content.links, vat_number: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default font-mono"
                    />
                  </div>
                  <div>
                    <Label>Charity Number</Label>
                    <Input
                      value={form.content.links.charity_number}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, links: { ...prev.content.links, charity_number: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default font-mono"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Source Type</Label>
                  <DynamicSelect
                    fieldType="source_type"
                    value={form.source}
                    onValueChange={(v) => setForm(prev => ({ ...prev, source: v }))}
                    placeholder="Select source"
                    triggerClassName="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Source URL</Label>
                  <Input
                    value={form.source_url}
                    onChange={(e) => setForm(prev => ({ ...prev, source_url: e.target.value }))}
                    placeholder="https://..."
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Confidence Score: {Math.round(form.confidence_score * 100)}%</Label>
                  <Slider
                    value={[form.confidence_score]}
                    onValueChange={([v]) => setForm(prev => ({ ...prev, confidence_score: v }))}
                    min={0}
                    max={1}
                    step={0.05}
                    className="mt-2"
                  />
                </div>
              </>
            )}
          </div>
        </ScrollArea>
        
        <DialogFooter className="border-t border-border-default pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!form.content.identity.legal_name}>
            {record ? 'Update' : 'Save'} Organisation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Person Modal Component
function PersonModal({ open, onOpenChange, record, onSave }) {
  const [activeTab, setActiveTab] = useState('identity');
  const [form, setForm] = useState({
    title: '',
    content: {
      identity: {
        full_name: '',
        known_aliases: [],
        current_title: '',
        all_job_titles: [],
        seniority_level: '',
        person_type: '',
        dob_month: '',
        dob_year: '',
        nationality: '',
        occupation: '',
        country_of_residence: '',
        disqualification_flag: false,
      },
      address: {
        address_line1: '',
        address_line2: '',
        town_city: '',
        county: '',
        postcode: '',
        country: 'England',
        is_correspondence: true,
      },
      companies_house: {
        officer_id: '',
        officer_url: '',
        appointment_history: [],
      },
      affiliations: [],
      links: {
        linked_persons_id: '',
        linked_audience_contact_id: '',
        linkedin_url: '',
      },
    },
    tags: [],
    sector: '',
    source: 'Manual',
    source_url: '',
    confidence_score: 0.8,
  });

  useEffect(() => {
    if (record) {
      setForm({
        title: record.title || '',
        content: {
          identity: { ...form.content.identity, ...record.content?.identity },
          address: { ...form.content.address, ...record.content?.address },
          companies_house: { ...form.content.companies_house, ...record.content?.companies_house },
          affiliations: record.content?.affiliations || [],
          links: { ...form.content.links, ...record.content?.links },
        },
        tags: record.tags || [],
        sector: record.content?.sector || '',
        source: record.source || 'Manual',
        source_url: record.content?.source_url || '',
        confidence_score: record.content?.confidence_score || 0.8,
      });
    } else {
      setForm({
        title: '',
        content: {
          identity: { full_name: '', known_aliases: [], current_title: '', all_job_titles: [], seniority_level: '', person_type: '', dob_month: '', dob_year: '', nationality: '', occupation: '', country_of_residence: '', disqualification_flag: false },
          address: { address_line1: '', address_line2: '', town_city: '', county: '', postcode: '', country: 'England', is_correspondence: true },
          companies_house: { officer_id: '', officer_url: '', appointment_history: [] },
          affiliations: [],
          links: { linked_persons_id: '', linked_audience_contact_id: '', linkedin_url: '' },
        },
        tags: [],
        sector: '',
        source: 'Manual',
        source_url: '',
        confidence_score: 0.8,
      });
    }
    setActiveTab('identity');
  }, [record, open]);

  const addAffiliation = () => {
    setForm(prev => ({
      ...prev,
      content: {
        ...prev.content,
        affiliations: [...prev.content.affiliations, { organisation_name: '', companies_house_number: '', role: '', appointed_on: '', resigned_on: '', is_current: true }]
      }
    }));
  };

  const updateAffiliation = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      content: {
        ...prev.content,
        affiliations: prev.content.affiliations.map((aff, i) => i === index ? { ...aff, [field]: value } : aff)
      }
    }));
  };

  const removeAffiliation = (index) => {
    setForm(prev => ({
      ...prev,
      content: {
        ...prev.content,
        affiliations: prev.content.affiliations.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = () => {
    const data = {
      title: form.content.identity.full_name || form.title,
      record_type: 'person',
      content: {
        schema_version: 'uk-intel-v1',
        ...form.content,
        sector: form.sector,
        confidence_score: form.confidence_score,
        source_url: form.source_url,
      },
      tags: form.tags,
      source: form.source,
    };
    onSave(data);
  };

  const tabs = [
    { id: 'identity', label: 'Identity' },
    { id: 'address', label: 'Address' },
    { id: 'companies_house', label: 'Companies House' },
    { id: 'affiliations', label: 'Current Affiliations' },
    { id: 'links', label: 'Links & Source' },
  ];

  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const YEARS = Array.from({ length: 80 }, (_, i) => (new Date().getFullYear() - i).toString());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-surface-card border-border-default max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-text-primary flex items-center gap-2">
            <User className="w-5 h-5 text-green-400" />
            {record ? 'Edit Person' : 'Record Person'}
          </DialogTitle>
        </DialogHeader>
        
        <ModalTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        
        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            {/* Tab 1: Identity */}
            {activeTab === 'identity' && (
              <>
                <div>
                  <Label>Full Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={form.content.identity.full_name}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, full_name: e.target.value } }
                    }))}
                    placeholder="Full legal name"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Known Aliases</Label>
                  <TagInput
                    value={form.content.identity.known_aliases}
                    onChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, known_aliases: v } }
                    }))}
                    placeholder="Add alias..."
                  />
                </div>
                
                <div>
                  <Label>Current Title</Label>
                  <Input
                    value={form.content.identity.current_title}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, current_title: e.target.value } }
                    }))}
                    placeholder="e.g., Chief Executive Officer"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>All Job Titles</Label>
                  <TagInput
                    value={form.content.identity.all_job_titles}
                    onChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, all_job_titles: v } }
                    }))}
                    placeholder="Add job title..."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Seniority Level</Label>
                    <DynamicSelect
                      fieldType="seniority_level"
                      value={form.content.identity.seniority_level}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, seniority_level: v } }
                      }))}
                      placeholder="Select level"
                      triggerClassName="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Person Type</Label>
                    <DynamicSelect
                      fieldType="person_type"
                      value={form.content.identity.person_type}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, person_type: v } }
                      }))}
                      placeholder="Select type"
                      triggerClassName="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Date of Birth (Month/Year only)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      value={form.content.identity.dob_month}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, dob_month: v } }
                      }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m, i) => <SelectItem key={m} value={(i + 1).toString()}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select
                      value={form.content.identity.dob_year}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, dob_year: v } }
                      }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nationality</Label>
                    <Input
                      value={form.content.identity.nationality}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, nationality: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Country of Residence</Label>
                    <Input
                      value={form.content.identity.country_of_residence}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, identity: { ...prev.content.identity, country_of_residence: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Occupation</Label>
                  <Input
                    value={form.content.identity.occupation}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, occupation: e.target.value } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-red-900/10 border border-red-800 rounded-lg">
                  <div>
                    <Label className="text-base text-red-400">Disqualification Flag</Label>
                    <p className="text-sm text-text-tertiary">Mark if person is disqualified from being a director</p>
                  </div>
                  <Switch
                    checked={form.content.identity.disqualification_flag}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, identity: { ...prev.content.identity, disqualification_flag: v } }
                    }))}
                  />
                </div>
              </>
            )}
            
            {/* Tab 2: Address */}
            {activeTab === 'address' && (
              <>
                <div className="bg-bg-tertiary/50 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-text-secondary mb-1">
                    <Home className="w-4 h-4" />
                    <span className="font-medium">Correspondence Address</span>
                  </div>
                  <p className="text-sm text-text-tertiary">Enter the person's correspondence or registered address.</p>
                </div>

                <div>
                  <Label>Address Line 1</Label>
                  <Input
                    value={form.content.address.address_line1}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, address: { ...prev.content.address, address_line1: e.target.value } }
                    }))}
                    placeholder="Street address"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Address Line 2</Label>
                  <Input
                    value={form.content.address.address_line2}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, address: { ...prev.content.address, address_line2: e.target.value } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Town / City</Label>
                    <Input
                      value={form.content.address.town_city}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, address: { ...prev.content.address, town_city: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>County</Label>
                    <Input
                      value={form.content.address.county}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, address: { ...prev.content.address, county: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Postcode</Label>
                    <Input
                      value={form.content.address.postcode}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, address: { ...prev.content.address, postcode: e.target.value.toUpperCase() } }
                      }))}
                      placeholder="e.g., SW1A 1AA"
                      className="bg-bg-tertiary border-border-default font-mono"
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Select
                      value={form.content.address.country}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, address: { ...prev.content.address, country: v } }
                      }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="England">England</SelectItem>
                        <SelectItem value="Wales">Wales</SelectItem>
                        <SelectItem value="Scotland">Scotland</SelectItem>
                        <SelectItem value="Northern Ireland">Northern Ireland</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={form.content.address.is_correspondence}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, address: { ...prev.content.address, is_correspondence: v } }
                    }))}
                  />
                  <Label>This is their correspondence address</Label>
                </div>
              </>
            )}
            
            {/* Tab 3: Companies House */}
            {activeTab === 'companies_house' && (
              <>
                <div>
                  <Label>Officer ID</Label>
                  <Input
                    value={form.content.companies_house.officer_id}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, companies_house: { ...prev.content.companies_house, officer_id: e.target.value } }
                    }))}
                    placeholder="Companies House officer identifier"
                    className="bg-bg-tertiary border-border-default font-mono"
                  />
                </div>
                
                <div>
                  <Label>Companies House Officer URL</Label>
                  <Input
                    value={form.content.companies_house.officer_url}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, companies_house: { ...prev.content.companies_house, officer_url: e.target.value } }
                    }))}
                    placeholder="https://find-and-update.company-information.service.gov.uk/officers/..."
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <p className="text-sm text-text-secondary mb-2">
                    Appointment history can be added after saving the person record. Use the "View Appointments" action to manage past roles.
                  </p>
                </div>
              </>
            )}
            
            {/* Tab 3: Current Affiliations */}
            {activeTab === 'affiliations' && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Current Affiliations</Label>
                    <p className="text-sm text-text-tertiary">Active roles at organisations</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={addAffiliation}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Affiliation
                  </Button>
                </div>
                
                {form.content.affiliations.length === 0 ? (
                  <div className="text-center py-8 text-text-tertiary">
                    <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No affiliations added yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.content.affiliations.map((aff, index) => (
                      <div key={index} className="p-4 bg-bg-tertiary rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Affiliation {index + 1}</Label>
                          <Button variant="ghost" size="sm" onClick={() => removeAffiliation(index)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={aff.organisation_name}
                            onChange={(e) => updateAffiliation(index, 'organisation_name', e.target.value)}
                            placeholder="Organisation name"
                            className="bg-bg-default border-border-default"
                          />
                          <Input
                            value={aff.companies_house_number}
                            onChange={(e) => updateAffiliation(index, 'companies_house_number', e.target.value)}
                            placeholder="CH Number"
                            className="bg-bg-default border-border-default font-mono"
                          />
                        </div>
                        <Input
                          value={aff.role}
                          onChange={(e) => updateAffiliation(index, 'role', e.target.value)}
                          placeholder="Role (e.g., Director, CEO)"
                          className="bg-bg-default border-border-default"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-text-tertiary">Appointed On</Label>
                            <Input
                              type="date"
                              value={aff.appointed_on}
                              onChange={(e) => updateAffiliation(index, 'appointed_on', e.target.value)}
                              className="bg-bg-default border-border-default"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-text-tertiary">Resigned On</Label>
                            <Input
                              type="date"
                              value={aff.resigned_on}
                              onChange={(e) => updateAffiliation(index, 'resigned_on', e.target.value)}
                              disabled={aff.is_current}
                              className="bg-bg-default border-border-default"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Is Current</Label>
                          <Switch
                            checked={aff.is_current}
                            onCheckedChange={(v) => updateAffiliation(index, 'is_current', v)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {/* Tab 4: Links & Source */}
            {activeTab === 'links' && (
              <>
                <div>
                  <Label>LinkedIn URL</Label>
                  <Input
                    value={form.content.links.linkedin_url}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, links: { ...prev.content.links, linkedin_url: e.target.value } }
                    }))}
                    placeholder="https://linkedin.com/in/..."
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Sector</Label>
                  <DynamicSelect
                    fieldType="sector"
                    value={form.sector}
                    onValueChange={(v) => setForm(prev => ({ ...prev, sector: v }))}
                    placeholder="Select sector"
                    triggerClassName="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Tags</Label>
                  <TagInput
                    value={form.tags}
                    onChange={(v) => setForm(prev => ({ ...prev, tags: v }))}
                  />
                </div>
                
                <div>
                  <Label>Source Type</Label>
                  <DynamicSelect
                    fieldType="source_type"
                    value={form.source}
                    onValueChange={(v) => setForm(prev => ({ ...prev, source: v }))}
                    placeholder="Select source"
                    triggerClassName="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Source URL</Label>
                  <Input
                    value={form.source_url}
                    onChange={(e) => setForm(prev => ({ ...prev, source_url: e.target.value }))}
                    placeholder="https://..."
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                
                <div>
                  <Label>Confidence Score: {Math.round(form.confidence_score * 100)}%</Label>
                  <Slider
                    value={[form.confidence_score]}
                    onValueChange={([v]) => setForm(prev => ({ ...prev, confidence_score: v }))}
                    min={0}
                    max={1}
                    step={0.05}
                    className="mt-2"
                  />
                </div>
              </>
            )}
          </div>
        </ScrollArea>
        
        <DialogFooter className="border-t border-border-default pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!form.content.identity.full_name}>
            {record ? 'Update' : 'Save'} Person
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Relationship Modal Component
function RelationshipModal({ open, onOpenChange, record, onSave, allRecords }) {
  const [form, setForm] = useState({
    entity_1_ref: '',
    entity_1_name: '',
    entity_1_type: '',
    entity_1_companies_house_number: '',
    relationship_type: '',
    entity_2_ref: '',
    entity_2_name: '',
    entity_2_type: '',
    entity_2_companies_house_number: '',
    start_date: '',
    end_date: '',
    is_current: true,
    strength: 0.8,
    evidence_refs: [],
    sector: '',
    tags: [],
    source: 'Manual',
    source_url: '',
    confidence_score: 0.8,
  });
  
  const [searchEntity1, setSearchEntity1] = useState('');
  const [searchEntity2, setSearchEntity2] = useState('');
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);

  useEffect(() => {
    if (record) {
      setForm({
        entity_1_ref: record.content?.entity_1_ref || '',
        entity_1_name: record.content?.entity_1_name || '',
        entity_1_type: record.content?.entity_1_type || '',
        entity_1_companies_house_number: record.content?.entity_1_companies_house_number || '',
        relationship_type: record.content?.relationship_type || '',
        entity_2_ref: record.content?.entity_2_ref || '',
        entity_2_name: record.content?.entity_2_name || '',
        entity_2_type: record.content?.entity_2_type || '',
        entity_2_companies_house_number: record.content?.entity_2_companies_house_number || '',
        start_date: record.content?.start_date || '',
        end_date: record.content?.end_date || '',
        is_current: record.content?.is_current ?? true,
        strength: record.content?.strength || 0.8,
        evidence_refs: record.content?.evidence_refs || [],
        sector: record.content?.sector || '',
        tags: record.tags || [],
        source: record.source || 'Manual',
        source_url: record.content?.source_url || '',
        confidence_score: record.content?.confidence_score || 0.8,
      });
      setSearchEntity1(record.content?.entity_1_name || '');
      setSearchEntity2(record.content?.entity_2_name || '');
    } else {
      setForm({
        entity_1_ref: '', entity_1_name: '', entity_1_type: '', entity_1_companies_house_number: '',
        relationship_type: '',
        entity_2_ref: '', entity_2_name: '', entity_2_type: '', entity_2_companies_house_number: '',
        start_date: '', end_date: '', is_current: true, strength: 0.8, evidence_refs: [],
        sector: '', tags: [], source: 'Manual', source_url: '', confidence_score: 0.8,
      });
      setSearchEntity1('');
      setSearchEntity2('');
    }
  }, [record, open]);

  const filteredRecords1 = useMemo(() => {
    if (!searchEntity1) return [];
    return allRecords.filter(r => 
      ['organisation', 'person', 'property'].includes(r.record_type) &&
      (r.title?.toLowerCase().includes(searchEntity1.toLowerCase()) ||
       r.companies_house_number?.includes(searchEntity1))
    ).slice(0, 10);
  }, [searchEntity1, allRecords]);

  const filteredRecords2 = useMemo(() => {
    if (!searchEntity2) return [];
    return allRecords.filter(r => 
      ['organisation', 'person', 'property'].includes(r.record_type) &&
      (r.title?.toLowerCase().includes(searchEntity2.toLowerCase()) ||
       r.companies_house_number?.includes(searchEntity2))
    ).slice(0, 10);
  }, [searchEntity2, allRecords]);

  const selectEntity1 = (entity) => {
    setForm(prev => ({
      ...prev,
      entity_1_ref: entity.id,
      entity_1_name: entity.title,
      entity_1_type: entity.record_type,
      entity_1_companies_house_number: entity.companies_house_number || '',
    }));
    setSearchEntity1(entity.title);
    setShowDropdown1(false);
  };

  const selectEntity2 = (entity) => {
    setForm(prev => ({
      ...prev,
      entity_2_ref: entity.id,
      entity_2_name: entity.title,
      entity_2_type: entity.record_type,
      entity_2_companies_house_number: entity.companies_house_number || '',
    }));
    setSearchEntity2(entity.title);
    setShowDropdown2(false);
  };

  const handleSave = () => {
    const title = `${form.entity_1_name} → ${form.relationship_type} → ${form.entity_2_name}`;
    const data = {
      title,
      record_type: 'relationship',
      content: {
        schema_version: 'uk-intel-v1',
        ...form,
      },
      tags: form.tags,
      source: form.source,
    };
    onSave(data);
  };

  const directionLabel = form.entity_1_name && form.relationship_type && form.entity_2_name
    ? `${form.entity_1_name} → ${form.relationship_type.toLowerCase().replace(/ /g, '_')} → ${form.entity_2_name}`
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-surface-card border-border-default max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-text-primary flex items-center gap-2">
            <Link2 className="w-5 h-5 text-pink-400" />
            {record ? 'Edit Relationship' : 'Map Relationship'}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1">
          <div className="space-y-4 pb-4">
            {/* Entity 1 */}
            <div className="relative">
              <Label>Entity 1 <span className="text-red-400">*</span></Label>
              <Input
                value={searchEntity1}
                onChange={(e) => { setSearchEntity1(e.target.value); setShowDropdown1(true); }}
                onFocus={() => setShowDropdown1(true)}
                placeholder="Search organisations, people, properties..."
                className="bg-bg-tertiary border-border-default"
              />
              {showDropdown1 && filteredRecords1.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-surface-card border border-border-default rounded-lg shadow-lg max-h-48 overflow-auto">
                  {filteredRecords1.map(entity => (
                    <div
                      key={entity.id}
                      onClick={() => selectEntity1(entity)}
                      className="p-2 hover:bg-bg-tertiary cursor-pointer flex items-center gap-2"
                    >
                      <EntityTypeBadge type={entity.record_type} size="sm" />
                      <span className="text-text-primary">{entity.title}</span>
                      {entity.companies_house_number && (
                        <span className="text-text-tertiary font-mono text-xs">{entity.companies_house_number}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {form.entity_1_name && (
                <div className="mt-2 flex items-center gap-2">
                  <EntityTypeBadge type={form.entity_1_type} />
                  <span className="text-text-primary">{form.entity_1_name}</span>
                  <Button variant="ghost" size="sm" onClick={() => { setForm(prev => ({ ...prev, entity_1_ref: '', entity_1_name: '', entity_1_type: '', entity_1_companies_house_number: '' })); setSearchEntity1(''); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            
            {/* Relationship Type */}
            <div>
              <Label>Relationship Type <span className="text-red-400">*</span></Label>
              <DynamicSelect
                fieldType="relationship_type"
                value={form.relationship_type}
                onValueChange={(v) => setForm(prev => ({ ...prev, relationship_type: v }))}
                placeholder="Select relationship type"
                triggerClassName="bg-bg-tertiary border-border-default"
              />
            </div>
            
            {/* Direction Label */}
            {directionLabel && (
              <div className="p-3 bg-accent-primary/10 border border-accent-primary/30 rounded-lg text-center">
                <p className="text-sm text-accent-primary font-mono">{directionLabel}</p>
              </div>
            )}
            
            {/* Entity 2 */}
            <div className="relative">
              <Label>Entity 2 <span className="text-red-400">*</span></Label>
              <Input
                value={searchEntity2}
                onChange={(e) => { setSearchEntity2(e.target.value); setShowDropdown2(true); }}
                onFocus={() => setShowDropdown2(true)}
                placeholder="Search organisations, people, properties..."
                className="bg-bg-tertiary border-border-default"
              />
              {showDropdown2 && filteredRecords2.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-surface-card border border-border-default rounded-lg shadow-lg max-h-48 overflow-auto">
                  {filteredRecords2.map(entity => (
                    <div
                      key={entity.id}
                      onClick={() => selectEntity2(entity)}
                      className="p-2 hover:bg-bg-tertiary cursor-pointer flex items-center gap-2"
                    >
                      <EntityTypeBadge type={entity.record_type} size="sm" />
                      <span className="text-text-primary">{entity.title}</span>
                      {entity.companies_house_number && (
                        <span className="text-text-tertiary font-mono text-xs">{entity.companies_house_number}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {form.entity_2_name && (
                <div className="mt-2 flex items-center gap-2">
                  <EntityTypeBadge type={form.entity_2_type} />
                  <span className="text-text-primary">{form.entity_2_name}</span>
                  <Button variant="ghost" size="sm" onClick={() => { setForm(prev => ({ ...prev, entity_2_ref: '', entity_2_name: '', entity_2_type: '', entity_2_companies_house_number: '' })); setSearchEntity2(''); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            
            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                  disabled={form.is_current}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
              <div>
                <Label className="text-base">Is Current</Label>
                <p className="text-sm text-text-tertiary">Relationship is currently active</p>
              </div>
              <Switch
                checked={form.is_current}
                onCheckedChange={(v) => setForm(prev => ({ ...prev, is_current: v, end_date: v ? '' : prev.end_date }))}
              />
            </div>
            
            <div>
              <Label>Strength: {Math.round(form.strength * 100)}%</Label>
              <Slider
                value={[form.strength]}
                onValueChange={([v]) => setForm(prev => ({ ...prev, strength: v }))}
                min={0}
                max={1}
                step={0.05}
                className="mt-2"
              />
              <p className="text-xs text-text-tertiary mt-1">Certainty or importance of this relationship</p>
            </div>
            
            <div>
              <Label>Sector</Label>
              <DynamicSelect
                fieldType="sector"
                value={form.sector}
                onValueChange={(v) => setForm(prev => ({ ...prev, sector: v }))}
                placeholder="Select sector"
                triggerClassName="bg-bg-tertiary border-border-default"
              />
            </div>
            
            <div>
              <Label>Tags</Label>
              <TagInput value={form.tags} onChange={(v) => setForm(prev => ({ ...prev, tags: v }))} />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source Type</Label>
                <DynamicSelect
                  fieldType="source_type"
                  value={form.source}
                  onValueChange={(v) => setForm(prev => ({ ...prev, source: v }))}
                  placeholder="Select source"
                  triggerClassName="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Confidence: {Math.round(form.confidence_score * 100)}%</Label>
                <Slider
                  value={[form.confidence_score]}
                  onValueChange={([v]) => setForm(prev => ({ ...prev, confidence_score: v }))}
                  min={0}
                  max={1}
                  step={0.05}
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        </ScrollArea>
        
        <DialogFooter className="border-t border-border-default pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!form.entity_1_name || !form.relationship_type || !form.entity_2_name}>
            Save Relationship
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Data Intelligence Component
export function DataIntelligence() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('organisation');
  
  // Records State
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  
  // Modal State
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Preview Sheet State
  const [previewRecord, setPreviewRecord] = useState(null);
  const [showPreviewSheet, setShowPreviewSheet] = useState(false);
  
  // Health Stats
  const [stats, setStats] = useState({
    organisations: 0,
    persons: 0,
    locations: 0,
    properties: 0,
    events: 0,
    relationships: 0,
    documents: 0,
    ch_anchored_pct: 0,
    geocoded_pct: 0,
    automated_pct: 0,
    added_this_week: 0,
  });

  useEffect(() => {
    loadStats();
    loadAllRecords();
  }, []);

  useEffect(() => {
    loadRecords();
  }, [activeTab, search, page]);

  const loadStats = async () => {
    try {
      const types = ['organisation', 'person', 'location', 'property', 'event', 'relationship', 'document'];
      const promises = types.map(type => 
        intelligenceAPI.getAll({ record_type: type, page_size: 1 })
      );
      const responses = await Promise.all(promises);
      
      const newStats = { ...stats };
      types.forEach((type, idx) => {
        newStats[type + 's'] = responses[idx].data.total || 0;
      });
      setStats(newStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadAllRecords = async () => {
    try {
      const response = await intelligenceAPI.getAll({ page_size: 100 });
      setAllRecords(response.data.records || []);
    } catch (error) {
      console.error('Failed to load all records:', error);
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const response = await intelligenceAPI.getAll({
        record_type: activeTab,
        search: search || undefined,
        page,
        page_size: 20,
      });
      setRecords(response.data.records || []);
      setRecordsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load records:', error);
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRecord = async (data) => {
    try {
      if (editingRecord) {
        await intelligenceAPI.update(editingRecord.id, data);
        toast.success('Record updated');
      } else {
        await intelligenceAPI.create(data);
        toast.success('Record created');
      }
      // Close all modals
      setShowOrgModal(false);
      setShowPersonModal(false);
      setShowRelationshipModal(false);
      setShowEventModal(false);
      setShowDocumentModal(false);
      setShowLocationModal(false);
      setShowPropertyModal(false);
      setEditingRecord(null);
      loadRecords();
      loadStats();
      loadAllRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save record');
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    try {
      await intelligenceAPI.delete(id);
      toast.success('Record deleted');
      loadRecords();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const openAddModal = () => {
    setEditingRecord(null);
    if (activeTab === 'organisation') {
      setShowOrgModal(true);
    } else if (activeTab === 'person') {
      setShowPersonModal(true);
    } else if (activeTab === 'relationship') {
      setShowRelationshipModal(true);
    } else if (activeTab === 'event') {
      setShowEventModal(true);
    } else if (activeTab === 'document') {
      setShowDocumentModal(true);
    } else if (activeTab === 'location') {
      setShowLocationModal(true);
    } else if (activeTab === 'property') {
      setShowPropertyModal(true);
    }
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    if (record.record_type === 'organisation') {
      setShowOrgModal(true);
    } else if (record.record_type === 'person') {
      setShowPersonModal(true);
    } else if (record.record_type === 'relationship') {
      setShowRelationshipModal(true);
    } else if (record.record_type === 'event') {
      setShowEventModal(true);
    } else if (record.record_type === 'document') {
      setShowDocumentModal(true);
    } else if (record.record_type === 'location') {
      setShowLocationModal(true);
    } else if (record.record_type === 'property') {
      setShowPropertyModal(true);
    }
  };

  // Handle row click to show preview
  const handleRowClick = (record) => {
    setPreviewRecord(record);
    setShowPreviewSheet(true);
  };

  // Handle edit from preview sheet
  const handleEditFromPreview = (record) => {
    setShowPreviewSheet(false);
    openEditModal(record);
  };

  // Helper to get address string from content
  const getAddressString = (content) => {
    if (!content) return '-';
    const parts = [
      content.address_line1 || content.address?.address_line1,
      content.town_city || content.address?.town_city,
      content.postcode || content.address?.postcode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  const getButtonText = () => {
    const type = RECORD_TYPES.find(t => t.value === activeTab);
    return type?.buttonText || '+ Add Record';
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return formatDate(dateStr);
  };

  // Render table based on active tab
  const renderTable = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={8} className="text-center py-12">
            <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
          </TableCell>
        </TableRow>
      );
    }
    
    if (records.length === 0) {
      const type = RECORD_TYPES.find(t => t.value === activeTab);
      const Icon = type?.icon || Building2;
      return (
        <TableRow>
          <TableCell colSpan={8} className="text-center py-16">
            <Icon className="w-12 h-12 mx-auto mb-3 text-text-tertiary opacity-30" />
            <p className="text-text-secondary mb-1">No {type?.label.toLowerCase()} in the intelligence graph yet</p>
            <p className="text-text-tertiary text-sm mb-4">Add {type?.label.toLowerCase()} manually, import from CSV, or run an automated story to populate this layer.</p>
            <Button onClick={openAddModal}>{getButtonText()}</Button>
          </TableCell>
        </TableRow>
      );
    }
    
    // Organisation Table
    if (activeTab === 'organisation') {
      return records.map((record) => (
        <TableRow 
          key={record.id} 
          className="table-row cursor-pointer hover:bg-bg-tertiary/50"
          onClick={() => handleRowClick(record)}
        >
          <TableCell className="table-cell">
            <div>
              <p className="font-medium text-text-primary">{record.title}</p>
              {record.content?.identity?.trading_names?.[0] && (
                <p className="text-xs text-text-tertiary">t/a {record.content.identity.trading_names[0]}</p>
              )}
            </div>
          </TableCell>
          <TableCell className="table-cell">
            {record.companies_house_number ? (
              <span className="font-mono text-sm text-blue-400">{record.companies_house_number}</span>
            ) : (
              <span className="text-text-tertiary" title="No CH number">—</span>
            )}
          </TableCell>
          <TableCell className="table-cell">
            <div className="flex flex-col gap-1">
              {record.content?.identity?.company_type && (
                <Badge variant="outline" className="w-fit text-xs">{record.content.identity.company_type}</Badge>
              )}
              {record.content?.identity?.company_status && (
                <Badge className={cn(
                  'w-fit text-xs border',
                  record.content.identity.company_status === 'Active' ? 'bg-green-900/20 text-green-400 border-green-800' :
                  record.content.identity.company_status === 'Dissolved' ? 'bg-slate-800 text-slate-400 border-slate-700' :
                  'bg-red-900/20 text-red-400 border-red-800'
                )}>
                  {record.content.identity.company_status}
                </Badge>
              )}
            </div>
          </TableCell>
          <TableCell className="table-cell">
            <RiskSignals content={record.content} />
          </TableCell>
          <TableCell className="table-cell">
            {record.content?.identity?.sector || '-'}
          </TableCell>
          <TableCell className="table-cell">
            <span className="text-sm" title={getAddressString(record.content?.address)}>
              {record.content?.address?.postcode_area && record.content?.address?.region ? (
                `${record.content.address.postcode_area} · ${record.content.address.region}`
              ) : record.content?.address?.town_city ? (
                record.content.address.town_city
              ) : (
                <span className="text-text-tertiary">—</span>
              )}
            </span>
          </TableCell>
          <TableCell className="table-cell">{formatRelativeTime(record.created_at)}</TableCell>
          <TableCell className="table-cell" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditModal(record)}>
                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Eye className="w-4 h-4 mr-2" /> View Related Records
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Zap className="w-4 h-4 mr-2" /> Add Event
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Link2 className="w-4 h-4 mr-2" /> Add Relationship
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDeleteRecord(record.id)} className="text-red-400">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
      ));
    }
    
    // Relationship Table
    if (activeTab === 'relationship') {
      return records.map((record) => (
        <TableRow 
          key={record.id} 
          className="table-row cursor-pointer hover:bg-bg-tertiary/50"
          onClick={() => handleRowClick(record)}
        >
          <TableCell className="table-cell">
            <div className="flex items-center gap-2">
              <EntityTypeBadge type={record.content?.entity_1_type} size="sm" />
              <span className="text-text-primary">{record.content?.entity_1_name || '-'}</span>
            </div>
          </TableCell>
          <TableCell className="table-cell text-center">
            <span className="text-text-tertiary text-sm">→ {record.content?.relationship_type?.toLowerCase().replace(/ /g, '_')} →</span>
          </TableCell>
          <TableCell className="table-cell">
            <div className="flex items-center gap-2">
              <EntityTypeBadge type={record.content?.entity_2_type} size="sm" />
              <span className="text-text-primary">{record.content?.entity_2_name || '-'}</span>
            </div>
          </TableCell>
          <TableCell className="table-cell">
            {record.content?.start_date ? formatDate(record.content.start_date) : '-'} → {record.content?.is_current ? 'Present' : (record.content?.end_date ? formatDate(record.content.end_date) : '-')}
          </TableCell>
          <TableCell className="table-cell">
            {record.content?.is_current ? (
              <Badge className="bg-green-900/20 text-green-400 border-green-800 border">Active</Badge>
            ) : (
              <Badge className="bg-slate-800 text-slate-400 border-slate-700 border">Historical</Badge>
            )}
          </TableCell>
          <TableCell className="table-cell">{formatRelativeTime(record.created_at)}</TableCell>
          <TableCell className="table-cell" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditModal(record)}>
                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Network className="w-4 h-4 mr-2" /> View Graph
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDeleteRecord(record.id)} className="text-red-400">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
      ));
    }
    
    // People Table
    if (activeTab === 'person') {
      return records.map((record) => (
        <TableRow 
          key={record.id} 
          className="table-row cursor-pointer hover:bg-bg-tertiary/50"
          onClick={() => handleRowClick(record)}
        >
          <TableCell className="table-cell">
            <div>
              <p className="font-medium text-text-primary">{record.title}</p>
              {record.content?.identity?.current_title && (
                <p className="text-xs text-text-tertiary">{record.content.identity.current_title}</p>
              )}
            </div>
          </TableCell>
          <TableCell className="table-cell">
            {record.content?.identity?.officer_id ? (
              <span className="font-mono text-sm text-blue-400">{record.content.identity.officer_id}</span>
            ) : (
              <span className="text-text-tertiary">—</span>
            )}
          </TableCell>
          <TableCell className="table-cell">
            {record.content?.affiliations?.[0]?.organisation_name || '-'}
          </TableCell>
          <TableCell className="table-cell">
            {record.content?.identity?.seniority_level && (
              <Badge className={cn(
                'text-xs border',
                record.content.identity.seniority_level === 'C-Suite' ? 'bg-amber-900/20 text-amber-400 border-amber-800' :
                record.content.identity.seniority_level === 'Director' ? 'bg-blue-900/20 text-blue-400 border-blue-800' :
                'bg-slate-800 text-slate-400 border-slate-700'
              )}>
                {record.content.identity.seniority_level}
              </Badge>
            )}
          </TableCell>
          <TableCell className="table-cell">
            {record.content?.identity?.person_type || '-'}
          </TableCell>
          <TableCell className="table-cell">
            <div className="flex gap-1">
              {record.content?.identity?.disqualification_flag && (
                <Badge className="bg-red-900/20 text-red-400 border-red-800 border text-xs">Disqualified</Badge>
              )}
            </div>
          </TableCell>
          <TableCell className="table-cell">{formatRelativeTime(record.created_at)}</TableCell>
          <TableCell className="table-cell" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditModal(record)}>
                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <User className="w-4 h-4 mr-2" /> Add to Audience Contacts
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Briefcase className="w-4 h-4 mr-2" /> View Appointments
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDeleteRecord(record.id)} className="text-red-400">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
      ));
    }
    
    // Default table for other types (Location, Property, Event, Document)
    return records.map((record) => (
      <TableRow 
        key={record.id} 
        className="table-row cursor-pointer hover:bg-bg-tertiary/50"
        onClick={() => handleRowClick(record)}
      >
        <TableCell className="table-cell font-medium">{record.title}</TableCell>
        <TableCell className="table-cell">
          <span className="text-sm text-text-secondary" title={getAddressString(record.content)}>
            {getAddressString(record.content)}
          </span>
        </TableCell>
        <TableCell className="table-cell">
          <Badge variant="outline" className="capitalize">{record.source || 'manual'}</Badge>
        </TableCell>
        <TableCell className="table-cell">{formatRelativeTime(record.created_at)}</TableCell>
        <TableCell className="table-cell" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" onClick={() => openEditModal(record)}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => handleDeleteRecord(record.id)}>
              <Trash2 className="w-4 h-4 text-red-400" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  // Get table headers based on active tab
  const getTableHeaders = () => {
    if (activeTab === 'organisation') {
      return ['Legal Name', 'CH Number', 'Type / Status', 'Risk Signals', 'Sector', 'Location', 'Date Added', ''];
    }
    if (activeTab === 'relationship') {
      return ['Entity 1', '→', 'Entity 2', 'Period', 'Status', 'Date Added', ''];
    }
    if (activeTab === 'person') {
      return ['Full Name', 'Officer ID', 'Primary Organisation', 'Seniority', 'Type', 'Flags', 'Date Added', ''];
    }
    if (activeTab === 'location' || activeTab === 'property') {
      return ['Title', 'Address', 'Source', 'Date Added', ''];
    }
    if (activeTab === 'event') {
      return ['Title', 'Location', 'Source', 'Date Added', ''];
    }
    return ['Title', 'Address', 'Source', 'Date Added', ''];
  };
if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return (
    <div className="space-y-6" data-testid="intelligence-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-text-primary uppercase tracking-tight">
          Data Intelligence
        </h1>
        <p className="text-text-secondary mt-1">Comprehensive data and intelligence records management</p>
      </div>

      {/* Intelligence Health Panel */}
      <Card className="bg-surface-card border-border-default">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2 cursor-pointer hover:text-accent-primary" onClick={() => setActiveTab('organisation')}>
              <Building2 className="w-4 h-4 text-blue-400" />
              <span className="text-text-primary font-medium">Orgs: {formatNumber(stats.organisations)}</span>
            </div>
            <span className="text-text-tertiary">|</span>
            <div className="flex items-center gap-2 cursor-pointer hover:text-accent-primary" onClick={() => setActiveTab('person')}>
              <User className="w-4 h-4 text-green-400" />
              <span className="text-text-primary font-medium">People: {formatNumber(stats.persons)}</span>
            </div>
            <span className="text-text-tertiary">|</span>
            <div className="flex items-center gap-2 cursor-pointer hover:text-accent-primary" onClick={() => setActiveTab('event')}>
              <Zap className="w-4 h-4 text-purple-400" />
              <span className="text-text-primary font-medium">Events: {formatNumber(stats.events)}</span>
            </div>
            <span className="text-text-tertiary">|</span>
            <div className="flex items-center gap-2 cursor-pointer hover:text-accent-primary" onClick={() => setActiveTab('relationship')}>
              <Link2 className="w-4 h-4 text-pink-400" />
              <span className="text-text-primary font-medium">Relationships: {formatNumber(stats.relationships)}</span>
            </div>
            <span className="text-text-tertiary">|</span>
            <div className="flex items-center gap-2 cursor-pointer hover:text-accent-primary" onClick={() => setActiveTab('document')}>
              <FileText className="w-4 h-4 text-teal-400" />
              <span className="text-text-primary font-medium">Documents: {formatNumber(stats.documents)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); setSearch(''); }}>
        <TabsList className="bg-bg-tertiary flex-wrap">
          {RECORD_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <TabsTrigger key={type.value} value={type.value} className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                {type.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {RECORD_TYPES.map((type) => (
          <TabsContent key={type.value} value={type.value} className="space-y-4 mt-6">
            {/* Graph View - Special Tab */}
            {type.value === 'graph' ? (
              <div className="space-y-4">
                <Card className="bg-surface-card border-border-default">
                  <CardHeader>
                    <CardTitle className="text-text-primary flex items-center gap-2">
                      <Network className="w-5 h-5 text-indigo-400" />
                      Relationship Graph
                    </CardTitle>
                    <p className="text-sm text-text-tertiary">
                      Interactive visualization of connections between entities in your intelligence database
                    </p>
                  </CardHeader>
                  <CardContent>
                    <RelationshipGraph
                      records={allRecords.filter(r => r.record_type !== 'relationship')}
                      relationships={allRecords.filter(r => r.record_type === 'relationship')}
                      onNodeClick={(node) => {
                        if (node.data?.id) {
                          setPreviewRecord(node.data);
                          setShowPreviewSheet(true);
                        }
                      }}
                      width={1100}
                      height={600}
                      className="w-full"
                    />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <>
                {/* Filters & Actions */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                      <Input
                        placeholder={`Search ${type.label.toLowerCase()}...`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 bg-bg-tertiary border-border-default"
                        data-testid="intelligence-search-input"
                      />
                    </div>
                  </div>
                  <Button onClick={openAddModal} data-testid="add-record-btn">
                    {type.buttonText}
                  </Button>
                </div>

                {/* Records Table */}
                <Card className="bg-surface-card border-border-default">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="table-header hover:bg-transparent">
                          {getTableHeaders().map((header, i) => (
                            <TableHead key={i}>{header}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {renderTable()}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Pagination */}
                {recordsTotal > 20 && (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-text-tertiary">
                      Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, recordsTotal)} of {recordsTotal}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page * 20 >= recordsTotal}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Organisation Modal */}
      <OrganisationModal
        open={showOrgModal}
        onOpenChange={setShowOrgModal}
        record={editingRecord?.record_type === 'organisation' ? editingRecord : null}
        onSave={handleSaveRecord}
      />

      {/* Person Modal */}
      <PersonModal
        open={showPersonModal}
        onOpenChange={setShowPersonModal}
        record={editingRecord?.record_type === 'person' ? editingRecord : null}
        onSave={handleSaveRecord}
      />

      {/* Relationship Modal */}
      <RelationshipModal
        open={showRelationshipModal}
        onOpenChange={setShowRelationshipModal}
        record={editingRecord?.record_type === 'relationship' ? editingRecord : null}
        onSave={handleSaveRecord}
        allRecords={allRecords}
      />

      {/* Event Modal */}
      <EventModal
        open={showEventModal}
        onOpenChange={setShowEventModal}
        record={editingRecord?.record_type === 'event' ? editingRecord : null}
        onSave={handleSaveRecord}
        allRecords={allRecords}
      />

      {/* Document Modal */}
      <DocumentModal
        open={showDocumentModal}
        onOpenChange={setShowDocumentModal}
        record={editingRecord?.record_type === 'document' ? editingRecord : null}
        onSave={handleSaveRecord}
        allRecords={allRecords}
      />

      {/* Location Modal */}
      <LocationModal
        open={showLocationModal}
        onOpenChange={setShowLocationModal}
        record={editingRecord?.record_type === 'location' ? editingRecord : null}
        onSave={handleSaveRecord}
      />

      {/* Property Modal */}
      <PropertyModal
        open={showPropertyModal}
        onOpenChange={setShowPropertyModal}
        record={editingRecord?.record_type === 'property' ? editingRecord : null}
        onSave={handleSaveRecord}
        allRecords={allRecords}
      />

      {/* Record Preview Sheet */}
      <RecordPreviewSheet
        record={previewRecord}
        open={showPreviewSheet}
        onOpenChange={setShowPreviewSheet}
        onEdit={handleEditFromPreview}
      />
    </div>
  );
}

export default DataIntelligence;
